import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiFilter, FiHeart, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, { productCategories } from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

function Shop() {
  const { addToCart, toggleWishlist, isInWishlist, wishlist } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsString = searchParams.toString();
  const [collection, setCollection] = useState(searchParams.get("collection") || "all");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [campaign, setCampaign] = useState(searchParams.get("status") || "all");
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedFit, setSelectedFit] = useState("all");
  const [stockStatus, setStockStatus] = useState("all");
  const [sort, setSort] = useState("picks");
  const [visibleCount, setVisibleCount] = useState(12);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [quickAdded, setQuickAdded] = useState(null);
  const [isMobileFilters, setIsMobileFilters] = useState(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 900px)").matches
  );
  const filterTriggerRef = useRef(null);
  const filterPanelRef = useRef(null);

  useEffect(() => {
    setCollection(searchParams.get("collection") || "all");
    setSearch(searchParams.get("search") || "");
    setCategory(searchParams.get("category") || "all");
    setCampaign(searchParams.get("status") || "all");
  }, [paramsString, searchParams]);

  useEffect(() => setVisibleCount(12), [collection, search, category, campaign, selectedSizes, selectedColors, selectedFit, stockStatus, sort]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 900px)");
    const updateViewport = (event) => setIsMobileFilters(event.matches);
    query.addEventListener?.("change", updateViewport);
    return () => query.removeEventListener?.("change", updateViewport);
  }, []);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const panel = filterPanelRef.current;
    const trigger = filterTriggerRef.current;
    const focusTimer = window.setTimeout(
      () => panel?.querySelector(".shop-filters__mobile-head button")?.focus(),
      0
    );
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      const focusable = [...panel.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.classList.add("nav-open");
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.classList.remove("nav-open");
      document.removeEventListener("keydown", handleKeyDown);
      trigger?.focus();
    };
  }, [filtersOpen]);

  const availableSizes = useMemo(() => [...new Set(productData.flatMap((product) => product.sizes))], []);
  const availableColors = useMemo(() => [...new Set(productData.flatMap((product) => product.colors))], []);
  const availableFits = useMemo(() => [...new Set(productData.map((product) => product.attributes.fit))], []);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    const wishlistOnly = searchParams.get("wishlist") === "true";

    const products = productData.filter((product) => {
      const matchesCollection = collection === "all" || product.gender.toLowerCase() === collection.toLowerCase();
      const matchesSearch = !query || [product.name, ...product.colors, product.category, product.attributes.material, ...product.tags].join(" ").toLowerCase().includes(query);
      const matchesCategory = category === "all" || product.category === category;
      const matchesCampaign = campaign === "all" || (campaign === "new-in" && product.tags.includes("new-in")) || (campaign === "sale" && product.tags.includes("sale")) || (campaign === "pre-order" && product.status === "Pre-order");
      const matchesSize = selectedSizes.length === 0 || selectedSizes.some((size) => product.sizes.includes(size));
      const matchesColor = selectedColors.length === 0 || selectedColors.some((color) => product.colors.includes(color));
      const matchesFit = selectedFit === "all" || product.attributes.fit === selectedFit;
      const matchesStock = stockStatus === "all" || product.status === stockStatus;
      const matchesWishlist = !wishlistOnly || wishlist.includes(product.id);
      return matchesCollection && matchesSearch && matchesCategory && matchesCampaign && matchesSize && matchesColor && matchesFit && matchesStock && matchesWishlist;
    });

    return [...products].sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "newest") return Number(b.tags.includes("new-in")) - Number(a.tags.includes("new-in")) || b.id - a.id;
      if (sort === "name") return a.name.localeCompare(b.name);
      return Number(b.tags.includes("featured")) - Number(a.tags.includes("featured")) || a.id - b.id;
    });
  }, [campaign, category, collection, search, searchParams, selectedColors, selectedFit, selectedSizes, sort, stockStatus, wishlist]);

  const toggleArrayFilter = (value, setter) => {
    setter((values) => values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  };

  const clearFilters = () => {
    setCollection("all");
    setCategory("all");
    setCampaign("all");
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedFit("all");
    setStockStatus("all");
    setSearch("");
    setSearchParams({});
  };

  const quickAdd = (product, size) => {
    addToCart(product.id, 1, size, product.colors[0]);
    setQuickAdded(product.id);
    window.setTimeout(() => setQuickAdded(null), 1800);
  };

  const activeFilterCount = selectedSizes.length + selectedColors.length + Number(category !== "all") + Number(selectedFit !== "all") + Number(stockStatus !== "all") + Number(campaign !== "all");
  const pageTitle = searchParams.get("wishlist") === "true" ? "Wishlist" : search ? `Results for “${search}”` : collection === "women" ? "Women" : collection === "men" ? "Men" : campaign === "new-in" ? "New in" : campaign === "sale" ? "Summer selection" : "Shop all";

  const filterPanel = (
    <aside
      id="shop-filters"
      className={`shop-filters ${filtersOpen ? "is-open" : ""}`}
      aria-label="Product filters"
      aria-hidden={isMobileFilters ? !filtersOpen : undefined}
      inert={isMobileFilters && !filtersOpen ? "" : undefined}
      ref={filterPanelRef}
    >
      <div className="shop-filters__mobile-head">
        <strong>Filters</strong>
        <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><FiX /></button>
      </div>
      <div className="shop-filters__title">
        <span>Filter by</span>
        {activeFilterCount > 0 && <button type="button" onClick={clearFilters}>Clear all</button>}
      </div>

      <details open>
        <summary>Product type <FiChevronDown /></summary>
        <div className="shop-filter-list">
          <label><input type="radio" name="category" checked={category === "all"} onChange={() => setCategory("all")} /> All products</label>
          {productCategories.map((item) => <label key={item}><input type="radio" name="category" checked={category === item} onChange={() => setCategory(item)} /> {item}</label>)}
        </div>
      </details>

      <details open>
        <summary>Size <FiChevronDown /></summary>
        <div className="shop-size-filter">
          {availableSizes.map((size) => <label key={size} className={selectedSizes.includes(size) ? "is-active" : ""}><input type="checkbox" checked={selectedSizes.includes(size)} onChange={() => toggleArrayFilter(size, setSelectedSizes)} /><span>{size}</span></label>)}
        </div>
      </details>

      <details>
        <summary>Colour <FiChevronDown /></summary>
        <div className="shop-filter-list shop-color-filter">
          {availableColors.map((color) => {
            const product = productData.find((item) => item.colors.includes(color));
            const swatch = product.colorOptions.find((option) => option.name === color);
            return <label key={color}><input type="checkbox" checked={selectedColors.includes(color)} onChange={() => toggleArrayFilter(color, setSelectedColors)} /><i style={{ background: swatch.hex }} /> {color}</label>;
          })}
        </div>
      </details>

      <details>
        <summary>Fit <FiChevronDown /></summary>
        <div className="shop-filter-list">
          <label><input type="radio" name="fit" checked={selectedFit === "all"} onChange={() => setSelectedFit("all")} /> All fits</label>
          {availableFits.map((fit) => <label key={fit}><input type="radio" name="fit" checked={selectedFit === fit} onChange={() => setSelectedFit(fit)} /> {fit}</label>)}
        </div>
      </details>

      <details>
        <summary>Status <FiChevronDown /></summary>
        <div className="shop-filter-list">
          {["all", "In stock", "Pre-order", "Sale"].map((status) => <label key={status}><input type="radio" name="stock" checked={stockStatus === status} onChange={() => setStockStatus(status)} /> {status === "all" ? "All statuses" : status}</label>)}
        </div>
      </details>
      <button className="shop-filters__apply button-primary" type="button" onClick={() => setFiltersOpen(false)}>Show {filteredProducts.length} pieces</button>
    </aside>
  );

  return (
    <main className="shop-page">
      <header className="shop-heading page-shell">
        <p className="eyebrow">The everyday catalogue</p>
        <h1>{pageTitle}</h1>
        <p>Filter by product type, size, colour, fit and status. Every listing includes composition, use, care and multiple original views.</p>
      </header>

      <nav className="shop-category-nav" aria-label="Shop collections">
        <Link to="/shop" className={collection === "all" && category === "all" ? "is-active" : ""}>All</Link>
        <Link to="/shop?status=new-in">New in</Link>
        <Link to="/shop?collection=women">Women</Link>
        <Link to="/shop?collection=men">Men</Link>
        <Link to="/shop?category=Knitwear">Knitwear</Link>
        <Link to="/shop?category=Dresses">Dresses</Link>
        <Link to="/shop?status=sale">Sale</Link>
      </nav>

      <div className="shop-toolbar page-shell">
        <button ref={filterTriggerRef} type="button" className="shop-filter-toggle" onClick={() => setFiltersOpen(true)} aria-expanded={filtersOpen} aria-controls="shop-filters"><FiFilter /> Filters {activeFilterCount > 0 && <span>{activeFilterCount}</span>}</button>
        <span>{filteredProducts.length} pieces</span>
        <label>Sort by <select value={sort} onChange={(event) => setSort(event.target.value)}><option value="picks">Our picks</option><option value="newest">Newest</option><option value="price-low">Price, low to high</option><option value="price-high">Price, high to low</option><option value="name">Alphabetically</option></select></label>
      </div>

      <div className="shop-layout page-shell">
        {filterPanel}
        <section className="shop-results" aria-live="polite">
          {filteredProducts.length ? (
            <>
              <div className="shop-grid">
                {filteredProducts.slice(0, visibleCount).map((product) => (
                  <article className="shop-card" key={product.id}>
                    <div className="shop-card__media">
                      <Link to={`/shopDetails/${product.id}`}><img src={product.image} alt={`${product.name} — ${product.colorName}`} loading="lazy" /></Link>
                      <div className="shop-card__badges">{product.badges.map((badge) => <span key={badge}>{badge}</span>)}</div>
                      <button type="button" className={isInWishlist(product.id) ? "is-active" : ""} onClick={() => toggleWishlist(product.id)} aria-label={`${isInWishlist(product.id) ? "Remove" : "Add"} ${product.name} ${isInWishlist(product.id) ? "from" : "to"} wishlist`}><FiHeart /></button>
                      <div className="shop-card__quick-add">
                        <span>{quickAdded === product.id ? "Added to bag" : "+ Quick add"}</span>
                        <div>{product.sizes.map((size) => <button type="button" key={size} onClick={() => quickAdd(product, size)}>{size}</button>)}</div>
                      </div>
                    </div>
                    <Link className="shop-card__info" to={`/shopDetails/${product.id}`}>
                      <div><h2>{product.name}</h2><p>{product.colorName}{product.colors.length > 1 && <span> +{product.colors.length - 1}</span>}</p></div>
                      <div className="shop-card__price"><span>{formatPrice(product.price)}</span>{product.compareAt && <del>{formatPrice(product.compareAt)}</del>}<small>{product.status}</small></div>
                    </Link>
                  </article>
                ))}
              </div>
              {visibleCount < filteredProducts.length && <button className="shop-load-more button-secondary" type="button" onClick={() => setVisibleCount((count) => count + 8)}>Load more</button>}
            </>
          ) : (
            <div className="shop-empty"><span>{siteConfig.logo.symbol}</span><h2>No pieces found</h2><p>Try removing a filter or searching with a broader term.</p><button className="button-secondary" type="button" onClick={clearFilters}>Clear filters</button></div>
          )}
        </section>
      </div>
      {filtersOpen && <button type="button" className="shop-filter-backdrop" onClick={() => setFiltersOpen(false)} aria-label="Close filters" />}
    </main>
  );
}

export default Shop;
